// Photos people send from their phones: one list, used by the upload pickers,
// the API's upload checks and the worker's check of the real file type. The
// phone sends the photo as it took it; our server turns it the right way up,
// strips where and when it was taken, and shrinks it.

/** Every format a phone camera saves in (iPhones save HEIC; Live Photos keep a HEIC still). */
export const PHOTO_TYPES = ["image/jpeg", "image/png", "image/heic", "image/heif", "image/webp", "image/avif"] as const;
export type PhotoType = (typeof PHOTO_TYPES)[number];

/** For a file picker: any image (phones then offer the camera), named HEIC and HEIF for desktops that don't know them. */
export const PHOTO_ACCEPT = "image/*,.heic,.heif";
/** A photo, or a PDF someone already has. */
export const DOCUMENT_ACCEPT = `${PHOTO_ACCEPT},application/pdf`;

/** Raw phone photos are 3 to 12 MB; the limit leaves room for the biggest. */
export const MAX_PHOTO_BYTES = 25 * 1024 * 1024;

const BY_EXTENSION: Record<string, PhotoType | "application/pdf"> = {
	jpg: "image/jpeg",
	jpeg: "image/jpeg",
	png: "image/png",
	heic: "image/heic",
	heif: "image/heif",
	webp: "image/webp",
	avif: "image/avif",
	pdf: "application/pdf",
};

/**
 * The type to declare for a picked file. Some Android phones give HEIC photos no
 * type at all; guessing "JPEG" would make the server's check of the real bytes
 * turn it down, so go by the name, then leave it to the server.
 */
export function photoTypeOf(file: { name: string; type: string }): string {
	const t = file.type.toLowerCase();
	if (t && t !== "application/octet-stream") return t === "image/jpg" ? "image/jpeg" : t;
	return BY_EXTENSION[file.name.split(".").pop()?.toLowerCase() ?? ""] ?? "image/jpeg";
}

export const isPhotoType = (t: string): t is PhotoType => (PHOTO_TYPES as readonly string[]).includes(t);

/** A document's name inside a sentence: "your passport", but "your photo ID" (capitals in a short form stay). */
export const inSentence = (name: string) =>
	/^[A-Z][a-z]/.test(name) ? name.charAt(0).toLowerCase() + name.slice(1) : name;
